import * as React from 'react';
import { Link } from '@tanstack/react-router';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, ChevronDown, Eye, FileText, MoreHorizontal, Upload, Download, Edit, Trash2, Lock, Unlock, Search, X } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { formatDocumentDate } from '@/utils/date-formatting'; // Added new import

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Document, DocumentType, DocumentVisibility, DatePrecision } from '../types'; // Added DatePrecision
import { DocumentsService } from '../services/documents-service';
import { Pagination, PaginationInfo } from '@/components/ui/pagination';
import { DocumentDeleteConfirmDialog } from './DocumentDeleteConfirmDialog';

const documentTypeColors: Record<DocumentType, string> = {
  article: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
  advertisement: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
  editorial: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300',
  photo: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  letter: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
  document: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300',
  other: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
};

const documentTypeLabels: Record<DocumentType, string> = {
  article: 'Article',
  advertisement: 'Advertisement',
  editorial: 'Editorial',
  photo: 'Photograph',
  letter: 'Letter',
  document: 'Document',
  other: 'Other',
};

const visibilityColors: Record<DocumentVisibility, string> = {
  public: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300',
  private: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
  restricted: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
};

const visibilityLabels: Record<DocumentVisibility, string> = {
  public: 'Public',
  private: 'Private',
  restricted: 'Restricted',
};

interface PaginationInfo {
  total: number;
  currentPage: number;
  totalPages: number;
  pageSize: number;
  fromCache: boolean;
  cacheStats?: any;
}

interface DocumentTableProps {
  data: Document[];
  onDocumentUpdate?: (document: Document) => void;
  paginationInfo?: PaginationInfo;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onFiltersChange?: (filters: any) => void;
  isLoading?: boolean;
}

export function DocumentTable({ 
  data, 
  onDocumentUpdate,
  paginationInfo,
  onPageChange,
  onPageSizeChange,
  onFiltersChange,
  isLoading = false
}: DocumentTableProps) {
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [documentToDelete, setDocumentToDelete] = React.useState<Document | null>(null);
  const [isDeleting, setIsDeleting] = React.useState(false);
  const { toast } = useToast();

  // Define search params type
  type SearchParams = {
    page?: number;
    size?: number;
    q?: string;
    type?: string;
    vis?: string;
    sort?: string;
    order?: 'asc' | 'desc';
  };

  // Get current URL search parameters using window.location for now
  const [searchParams, setSearchParams] = React.useState<SearchParams>(() => {
    if (typeof window === 'undefined') return {};
    const urlParams = new URLSearchParams(window.location.search);
    return {
      page: urlParams.get('page') ? Number(urlParams.get('page')) : undefined,
      size: urlParams.get('size') ? Number(urlParams.get('size')) : undefined,
      q: urlParams.get('q') || undefined,
      type: urlParams.get('type') || undefined,
      vis: urlParams.get('vis') || undefined,
      sort: urlParams.get('sort') || undefined,
      order: (urlParams.get('order') as 'asc' | 'desc') || undefined,
    };
  });


  // Update searchParams when URL changes (only listen to popstate events)
  React.useEffect(() => {
    const updateParams = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const newParams = {
        page: urlParams.get('page') ? Number(urlParams.get('page')) : undefined,
        size: urlParams.get('size') ? Number(urlParams.get('size')) : undefined,
        q: urlParams.get('q') || undefined,
        type: urlParams.get('type') || undefined,
        vis: urlParams.get('vis') || undefined,
        sort: urlParams.get('sort') || undefined,
        order: (urlParams.get('order') as 'asc' | 'desc') || undefined,
      };
      
      // Only update if there's an actual change to prevent infinite loops
      setSearchParams(prevParams => {
        const hasChanged = JSON.stringify(prevParams) !== JSON.stringify(newParams);
        return hasChanged ? newParams : prevParams;
      });
    };

    window.addEventListener('popstate', updateParams);
    return () => window.removeEventListener('popstate', updateParams);
  }, []);

  // Extract current state from URL with defaults
  const currentPage = searchParams.page || 1;
  const pageSize = searchParams.size || 20; // Match the server-side default
  const searchQuery = searchParams.q || '';
  const typeFilter = searchParams.type ? searchParams.type.split(',') : [];
  const visibilityFilter = searchParams.vis ? searchParams.vis.split(',') : [];
  const sortBy = searchParams.sort || '';
  const sortOrder = searchParams.order || 'asc';

  // Initialize table state from URL parameters
  const [sorting, setSorting] = React.useState<SortingState>(() => {
    if (sortBy) {
      return [{ id: sortBy, desc: sortOrder === 'desc' }];
    }
    return [];
  });

  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);

  // Update column filters when URL parameters change (memoized to prevent infinite loops)
  React.useEffect(() => {
    const filters: ColumnFiltersState = [];
    if (searchQuery) {
      filters.push({ id: 'title', value: searchQuery });
    }
    if (typeFilter.length > 0) {
      filters.push({ id: 'document_type', value: typeFilter });
    }
    if (visibilityFilter.length > 0) {
      filters.push({ id: 'visibility', value: visibilityFilter });
    }
    
    // Only update if filters actually changed
    setColumnFilters(prevFilters => {
      const hasChanged = JSON.stringify(prevFilters) !== JSON.stringify(filters);
      return hasChanged ? filters : prevFilters;
    });
  }, [searchQuery, typeFilter.join(','), visibilityFilter.join(',')]);

  // Update sorting when URL parameters change (memoized to prevent infinite loops)
  React.useEffect(() => {
    const newSorting = sortBy ? [{ id: sortBy, desc: sortOrder === 'desc' }] : [];
    
    setSorting(prevSorting => {
      const hasChanged = JSON.stringify(prevSorting) !== JSON.stringify(newSorting);
      return hasChanged ? newSorting : prevSorting;
    });
  }, [sortBy, sortOrder]);

  const updateUrlParams = (newParams: Partial<SearchParams>) => {
    const updatedParams = { ...searchParams, ...newParams };
    const urlParams = new URLSearchParams();
    
    // Only add non-undefined values to URL
    Object.entries(updatedParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        urlParams.set(key, String(value));
      }
    });

    const newUrl = `${window.location.pathname}?${urlParams.toString()}`;
    window.history.pushState({}, '', newUrl);
    setSearchParams(updatedParams);
  };

  const handlePageChange = (page: number) => {
    updateUrlParams({ page: page === 1 ? undefined : page });
    // Call parent's page change handler for server-side pagination
    if (onPageChange) {
      onPageChange(page);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    updateUrlParams({ 
      size: newPageSize === 20 ? undefined : newPageSize, // Default is 20
      page: undefined // Reset to first page when changing page size
    });
    if (onPageSizeChange) {
      onPageSizeChange(newPageSize);
    }
  };

  const [searchInputValue, setSearchInputValue] = React.useState(searchQuery);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Update URL params and trigger search
    React.startTransition(() => {
      updateUrlParams({ 
        q: searchInputValue || undefined,
        page: undefined // Reset to first page when searching
      });
      
      // Notify parent for server-side filtering
      if (onFiltersChange) {
        onFiltersChange({
          search_term: searchInputValue || undefined,
          document_type: typeFilter.length > 0 ? typeFilter : undefined,
          visibility: visibilityFilter.length > 0 ? visibilityFilter : undefined,
          sort_by: sortBy || undefined,
          sort_order: sortOrder
        });
      }
    });
  };

  const handleSearchClear = () => {
    setSearchInputValue('');
    React.startTransition(() => {
      updateUrlParams({ 
        q: undefined,
        page: undefined
      });
      
      if (onFiltersChange) {
        onFiltersChange({
          search_term: undefined,
          document_type: typeFilter.length > 0 ? typeFilter : undefined,
          visibility: visibilityFilter.length > 0 ? visibilityFilter : undefined,
          sort_by: sortBy || undefined,
          sort_order: sortOrder
        });
      }
    });
  };

  // Update search input when URL changes
  React.useEffect(() => {
    setSearchInputValue(searchQuery);
  }, [searchQuery]);

  const handleTypeFilterChange = (types: string[]) => {
    updateUrlParams({ 
      type: types.length > 0 ? types.join(',') : undefined,
      page: undefined // Reset to first page when filtering
    });
    
    // Notify parent of filter changes for server-side filtering
    if (onFiltersChange) {
      onFiltersChange({
        search_term: searchQuery || undefined,
        document_type: types.length > 0 ? types : undefined,
        visibility: visibilityFilter.length > 0 ? visibilityFilter : undefined,
        sort_by: sortBy || undefined,
        sort_order: sortOrder
      });
    }
  };

  const handleVisibilityFilterChange = (visibility: string[]) => {
    updateUrlParams({ 
      vis: visibility.length > 0 ? visibility.join(',') : undefined,
      page: undefined // Reset to first page when filtering
    });
    
    // Notify parent of filter changes for server-side filtering
    if (onFiltersChange) {
      onFiltersChange({
        search_term: searchQuery || undefined,
        document_type: typeFilter.length > 0 ? typeFilter : undefined,
        visibility: visibility.length > 0 ? visibility : undefined,
        sort_by: sortBy || undefined,
        sort_order: sortOrder
      });
    }
  };

  const handleSortChange = (column: string, order: 'asc' | 'desc') => {
    updateUrlParams({ 
      sort: column || undefined,
      order: order === 'asc' ? undefined : order // Default is asc, so only store desc
    });
    
    // Trigger server-side sorting across all documents
    if (onFiltersChange) {
      onFiltersChange({
        search_term: searchQuery || undefined,
        document_type: typeFilter.length > 0 ? typeFilter : undefined,
        visibility: visibilityFilter.length > 0 ? visibilityFilter : undefined,
        sort_by: column,
        sort_order: order
      });
    }
  };


  const handleDownload = async (doc: Document) => {
    try {
      // Create a download URL based on the document's storage path
      const downloadUrl = `/api/documents/${doc.id}/download`;
      
      // Create a temporary link and trigger download
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = `${doc.title}.${doc.media.file_type.split('/')[1] || 'pdf'}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: 'Download Started',
        description: `Downloading ${doc.title}`,
      });
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: 'Download Failed',
        description: 'Failed to download the document. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleVisibilityToggle = async (document: Document) => {
    try {
      const newVisibility = document.visibility === 'public' ? 'private' : 'public';
      const updatedDocument = await DocumentsService.updateDocument(document.id, {
        visibility: newVisibility
      });
      
      toast({
        title: 'Visibility Updated',
        description: `Document is now ${newVisibility}`,
      });
      
      // Notify parent component of the update
      if (onDocumentUpdate) {
        onDocumentUpdate(updatedDocument);
      }
    } catch (error) {
      console.error('Failed to update visibility:', error);
      toast({
        title: 'Update Failed',
        description: 'Failed to update document visibility. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async (document: Document) => {
    // Check if document is already private
    if (document.visibility === 'private') {
      // Show confirmation dialog for permanent deletion
      setDocumentToDelete(document);
      setDeleteDialogOpen(true);
    } else {
      // Soft delete: set visibility to private
      try {
        const updatedDocument = await DocumentsService.updateDocument(document.id, {
          visibility: 'private'
        });
        
        toast({
          title: 'Document Archived',
          description: 'Document has been moved to private (archived)',
        });
        
        // Notify parent component of the update
        if (onDocumentUpdate) {
          onDocumentUpdate(updatedDocument);
        }
      } catch (error) {
        console.error('Failed to archive document:', error);
        toast({
          title: 'Archive Failed',
          description: 'Failed to archive document. Please try again.',
          variant: 'destructive',
        });
      }
    }
  };

  const handlePermanentDelete = async () => {
    if (!documentToDelete) return;

    setIsDeleting(true);
    try {
      // Call the delete API to permanently remove the document
      await DocumentsService.deleteDocument(documentToDelete.id);
      
      toast({
        title: 'Document Permanently Deleted',
        description: 'Document and all related data have been permanently removed',
      });
      
      // Close the dialog
      setDeleteDialogOpen(false);
      setDocumentToDelete(null);
      
      // Refresh the data by notifying parent
      // Since the document is deleted, we can't pass it to onDocumentUpdate
      // Instead, the parent component should refetch the data
      window.location.reload(); // Simple approach, could be improved with better state management
      
    } catch (error) {
      console.error('Failed to permanently delete document:', error);
      
      // Check if this is a 404 error, which might mean the DELETE endpoint isn't implemented
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      
      if (errorMessage.includes('404')) {
        toast({
          title: 'Delete Feature Not Available',
          description: 'Permanent deletion is not currently available on the server. The document will remain as private.',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Delete Failed',
          description: 'Failed to permanently delete document. Please try again.',
          variant: 'destructive',
        });
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: ColumnDef<Document>[] = [
    {
      id: 'select',
      header: ({ table }) => (
        <Checkbox
          checked={
            table.getIsAllPageRowsSelected() ||
            (table.getIsSomePageRowsSelected() && 'indeterminate')
          }
          onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
          aria-label="Select all"
        />
      ),
      cell: ({ row }) => (
        <Checkbox
          checked={row.getIsSelected()}
          onCheckedChange={(value) => row.toggleSelected(!!value)}
          aria-label="Select row"
        />
      ),
      enableSorting: false,
      enableHiding: false,
    },
    {
      accessorKey: 'title',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => {
              const isAsc = column.getIsSorted() === 'asc';
              column.toggleSorting(isAsc);
              handleSortChange('title', isAsc ? 'desc' : 'asc');
            }}
          >
            Document
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const document = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-muted flex items-center justify-center">
              <FileText className="h-5 w-5 text-muted-foreground" />
            </div>
            <div className="flex flex-col">
              <span className="font-medium">{document.title}</span>
              <span className="text-muted-foreground text-sm truncate max-w-[300px]">
                {document.description || 'No description'}
              </span>
            </div>
          </div>
        );
      },
      size: 300,
      filterFn: (row, _id, value) => {
        if (!value) return true;
        const searchValue = value.toLowerCase();
        const title = row.original.title?.toLowerCase() || '';
        const description = row.original.description?.toLowerCase() || '';
        return title.includes(searchValue) || description.includes(searchValue);
      },
    },
    {
      accessorKey: 'document_type',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => {
              const isAsc = column.getIsSorted() === 'asc';
              column.toggleSorting(isAsc);
              handleSortChange('document_type', isAsc ? 'desc' : 'asc');
            }}
          >
            Type
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const type = row.getValue('document_type') as DocumentType;
        return (
          <Badge className={documentTypeColors[type]} variant="outline">
            {documentTypeLabels[type]}
          </Badge>
        );
      },
      filterFn: (row, id, value) => {
        if (!value || value.length === 0) return true;
        const rowValue = row.getValue(id);
        return value.includes(rowValue);
      },
    },
    {
      accessorKey: 'original_date',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => {
              const isAsc = column.getIsSorted() === 'asc';
              column.toggleSorting(isAsc);
              handleSortChange('original_date', isAsc ? 'desc' : 'asc');
            }}
          >
            Date
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const rawDate = row.getValue('original_date') as string;
        const precision = row.original.original_date_precision as DatePrecision | undefined;
        const formattedDate = formatDocumentDate(rawDate, precision);
        return <div className="text-sm">{formattedDate}</div>;
      },
    },
    {
      accessorKey: 'id',
      header: ({ column }) => {
        return (
          <Button
            variant="ghost"
            onClick={() => {
              const isAsc = column.getIsSorted() === 'asc';
              column.toggleSorting(isAsc);
              handleSortChange('id', isAsc ? 'desc' : 'asc');
            }}
          >
            ID
            <ArrowUpDown className="ml-2 h-4 w-4" />
          </Button>
        );
      },
      cell: ({ row }) => {
        const id = row.getValue('id') as number;
        return (
          <div className="text-sm font-mono">
            {id}
          </div>
        );
      },
      size: 80,
    },
    {
      accessorKey: 'visibility',
      header: 'Visibility',
      cell: ({ row }) => {
        const visibility = row.getValue('visibility') as DocumentVisibility;
        return (
          <Badge className={visibilityColors[visibility]} variant="outline">
            {visibilityLabels[visibility]}
          </Badge>
        );
      },
      filterFn: (row, id, value) => {
        if (!value || value.length === 0) return true;
        const rowValue = row.getValue(id);
        return value.includes(rowValue);
      },
    },
    {
      accessorKey: 'audit.created_at',
      header: 'Added',
      cell: ({ row }) => {
        const timestamp = row.original.audit.created_at;
        const date = new Date(timestamp * 1000); // Convert Unix timestamp to JS Date
        return (
          <div className="text-sm" title={new Date(timestamp * 1000).toLocaleString()}>
            {formatDistanceToNow(date, { addSuffix: true })}
          </div>
        );
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const document = row.original;

        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Open menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>Actions</DropdownMenuLabel>
              <DropdownMenuItem onClick={() => window.location.href = `/documents/${document.id}`}>
                <Eye className="mr-2 h-4 w-4" />
                View details
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => window.location.href = `/documents/edit/${document.id}`}>
                <Edit className="mr-2 h-4 w-4" />
                Edit document
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => handleDownload(document)}>
                <Download className="mr-2 h-4 w-4" />
                Download document
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleVisibilityToggle(document)}>
                {document.visibility === 'public' ? (
                  <>
                    <Lock className="mr-2 h-4 w-4" />
                    Make private
                  </>
                ) : (
                  <>
                    <Unlock className="mr-2 h-4 w-4" />
                    Make public
                  </>
                )}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(document)}>
                <Trash2 className="mr-2 h-4 w-4" />
                Delete document
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  // Determine if cache is disabled by checking if we have fake cache stats
  const isCacheDisabled = paginationInfo?.cacheStats && 
    paginationInfo.cacheStats.hitRate === 0 && 
    paginationInfo.cacheStats.cacheHits === 0 && 
    paginationInfo.cacheStats.cacheMisses === 0;

  const table = useReactTable({
    data,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
    },
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 1000, // Large number to ensure all data is shown
      },
    },
    manualPagination: true, // We handle pagination server-side
    manualFiltering: true, // We handle filtering server-side across all documents
    manualSorting: !isCacheDisabled, // If cache disabled, let table handle sorting; if enabled, handle server-side
  });

  // Sync table pagination state with URL when it changes (prevent infinite loops)
  React.useEffect(() => {
    const tablePageIndex = table.getState().pagination.pageIndex;
    const urlPageIndex = currentPage - 1;
    
    if (tablePageIndex !== urlPageIndex) {
      table.setPageIndex(urlPageIndex);
    }
  }, [currentPage]); // Remove table from dependencies to prevent infinite loops

  // Use server-side pagination info if available, otherwise fallback to client-side
  const totalPages = paginationInfo?.totalPages || table.getPageCount();
  const totalItems = paginationInfo?.total || table.getFilteredRowModel().rows.length;
  const serverCurrentPage = paginationInfo?.currentPage || currentPage;

  return (
    <div className="w-full">
      {isLoading && (
        <div className="text-center py-4">
          <div className="text-sm text-muted-foreground">Loading documents...</div>
        </div>
      )}
      
      <div className="flex items-center justify-between py-4">
        <div className="flex items-center gap-2">
          {/* Search Input */}
          <form onSubmit={handleSearchSubmit} className="relative">
            <div className="relative">
              <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Search by title or ID..."
                value={searchInputValue}
                onChange={(e) => setSearchInputValue(e.target.value)}
                className="pl-8 pr-8 w-64"
              />
              {searchInputValue && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 p-0 hover:bg-muted"
                  onClick={handleSearchClear}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
          </form>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Type <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(documentTypeLabels).map(([type, label]) => (
                <DropdownMenuCheckboxItem
                  key={type}
                  checked={typeFilter.length === 0 || typeFilter.includes(type)}
                  onCheckedChange={(value) => {
                    let newTypes: string[];
                    if (typeFilter.length === 0) {
                      // If no filters are active and user unchecks an item, 
                      // show all items EXCEPT the unchecked one
                      if (value) {
                        newTypes = [type]; // Just this type
                      } else {
                        // Show all types except this one
                        newTypes = Object.keys(documentTypeLabels).filter(t => t !== type);
                      }
                    } else {
                      // If filters are active, toggle this specific item
                      if (value) {
                        newTypes = [...typeFilter, type];
                      } else {
                        newTypes = typeFilter.filter((val: string) => val !== type);
                      }
                    }
                    handleTypeFilterChange(newTypes);
                  }}
                >
                  {label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Visibility <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {Object.entries(visibilityLabels).map(([visibility, label]) => (
                <DropdownMenuCheckboxItem
                  key={visibility}
                  checked={visibilityFilter.length === 0 || visibilityFilter.includes(visibility)}
                  onCheckedChange={(value) => {
                    let newVisibility: string[];
                    if (visibilityFilter.length === 0) {
                      // If no filters are active and user unchecks an item,
                      // show all items EXCEPT the unchecked one
                      if (value) {
                        newVisibility = [visibility]; // Just this visibility
                      } else {
                        // Show all visibilities except this one
                        newVisibility = Object.keys(visibilityLabels).filter(v => v !== visibility);
                      }
                    } else {
                      // If filters are active, toggle this specific item
                      if (value) {
                        newVisibility = [...visibilityFilter, visibility];
                      } else {
                        newVisibility = visibilityFilter.filter((val: string) => val !== visibility);
                      }
                    }
                    handleVisibilityFilterChange(newVisibility);
                  }}
                >
                  {label}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="ml-auto">
            Export
          </Button>
          <Button asChild size="sm">
            <Link to="/documents/add">
              <Upload className="mr-2 h-4 w-4" />
              Add Document
            </Link>
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="ml-auto">
                Columns <ChevronDown className="ml-2 h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {table
                .getAllColumns()
                .filter((column) => column.getCanHide())
                .map((column) => {
                  return (
                    <DropdownMenuCheckboxItem
                      key={column.id}
                      className="capitalize"
                      checked={column.getIsVisible()}
                      onCheckedChange={(value) =>
                        column.toggleVisibility(!!value)
                      }
                    >
                      {column.id}
                    </DropdownMenuCheckboxItem>
                  );
                })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <TableHead key={header.id}>
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext()
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center">
                  No documents found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-between py-4">
        <div className="flex-1">
          <div className="text-sm text-muted-foreground">
            Showing {((serverCurrentPage - 1) * pageSize) + 1} to {Math.min(serverCurrentPage * pageSize, totalItems)} of {totalItems} documents
            {paginationInfo?.fromCache && ' (from cache)'}
          </div>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-muted-foreground">
            {table.getFilteredSelectedRowModel().rows.length} of{' '}
            {data.length} row(s) selected.
          </div>
          <Pagination
            currentPage={serverCurrentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
            showFirstLast={true}
            siblingCount={1}
          />
        </div>
      </div>
      
      <DocumentDeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        document={documentToDelete}
        onConfirm={handlePermanentDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
}
